import React, { useEffect, useState, createContext, useContext, useRef, useCallback, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Context para compartilhar status da assinatura
const SubscriptionContext = createContext(null);

export const useSubscription = () => useContext(SubscriptionContext);

// Rotas que não precisam de verificação de assinatura
const PUBLIC_ROUTES = [
  '/',
  '/comprar',
  '/admin/login',
  '/register',
  '/register-new',
  '/forgot-password',
  '/reset-password',
  '/superadmin',
  '/superadmin/login',
  '/superadmin/dashboard',
  '/superadmin/security',
  '/superadmin/company-modules',
  '/superadmin/data-management',
  '/superadmin/payment-terminals',
  '/onboarding/choose',
  '/onboarding/create-company',
  '/onboarding/join-company',
  '/onboarding/pending-approval',
  '/subscription/payment',
  '/clear-cache',
  '/device-selector'
];

// Prefixos de páginas públicas voltadas ao CLIENTE final (delivery, cardápio de mesa, reserva de hotel)
// — nunca devem cair em checagem de assinatura/login, mesmo com um token antigo salvo no navegador.
const PUBLIC_ROUTE_PREFIXES = ['/d/', '/m/', '/cardapio/', '/reservar/', '/booking/'];

// Verifica se a rota atual é pública
const isPublicRoute = (pathname) => {
  if (PUBLIC_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
    return true;
  }
  return PUBLIC_ROUTES.some(route => 
    pathname === route || 
    pathname.startsWith('/superadmin/') ||
    pathname.startsWith('/onboarding/')
  );
};

export function SubscriptionProvider({ children }) {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // ⚡ OTIMIZAÇÃO: Cache da última verificação
  const lastCheckRef = useRef(0);
  const lastStatusRef = useRef(null);

  const checkSubscription = useCallback(async (force = false) => {
    const token = localStorage.getItem('token');
    
    // Se não tem token ou é rota pública, não verifica
    if (!token || isPublicRoute(location.pathname)) {
      setLoading(false);
      setChecked(true);
      return;
    }

    // ⚡ OTIMIZAÇÃO: Não verificar se já verificou nos últimos 5 minutos (a menos que force)
    const now = Date.now();
    if (!force && lastStatusRef.current && (now - lastCheckRef.current) < 300000) {
      setSubscriptionStatus(lastStatusRef.current);
      setLoading(false);
      setChecked(true);
      return;
    }

    try {
      const res = await axios.get(`${API}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000 // 10 segundos de timeout
      });
      
      setSubscriptionStatus(res.data);
      lastStatusRef.current = res.data;
      lastCheckRef.current = now;
      
      // Se bloqueado, redireciona para página de pagamento
      if (res.data.blocked && !isPublicRoute(location.pathname)) {
        navigate('/subscription/payment', { 
          state: { subscription: res.data.subscription },
          replace: true 
        });
      }
    } catch (error) {
      console.error('Erro ao verificar assinatura:', error);
      // Se der erro 401, provavelmente token expirou
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin/login', { replace: true });
      } else {
        // Para outros erros (timeout, rede, etc), permitir acesso mas logar erro
        console.warn('Não foi possível verificar assinatura, permitindo acesso temporário');
        setSubscriptionStatus({ status: 'unknown', blocked: false });
      }
    } finally {
      setLoading(false);
      setChecked(true);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    checkSubscription();
  }, [location.pathname, checkSubscription]);

  // Mostra loading apenas na primeira verificação
  if (loading && !checked && !isPublicRoute(location.pathname)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-600 text-sm">Verificando assinatura...</p>
        </div>
      </div>
    );
  }

  return (
    <SubscriptionContext.Provider value={{ 
      subscriptionStatus, 
      checkSubscription: () => checkSubscription(true),
      isBlocked: subscriptionStatus?.blocked,
      daysRemaining: subscriptionStatus?.days_remaining,
      status: subscriptionStatus?.status
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

// ⚡ Componente de banner para mostrar dias restantes do trial (memoizado)
export const TrialBanner = memo(function TrialBanner() {
  const subscription = useSubscription();
  
  if (!subscription || subscription.status !== 'trial') {
    return null;
  }

  const { daysRemaining } = subscription;
  
  if (daysRemaining === undefined || daysRemaining > 3) {
    return null;
  }

  const bgColor = daysRemaining <= 1 ? 'bg-red-500' : daysRemaining <= 2 ? 'bg-orange-500' : 'bg-yellow-500';

  return (
    <div className={`${bgColor} text-white text-center py-2 px-4 text-sm font-medium`}>
      {daysRemaining === 0 ? (
        <>Seu período de teste expira hoje! <a href="/subscription/payment" className="underline font-bold">Assine agora</a></>
      ) : daysRemaining === 1 ? (
        <>Seu período de teste expira amanhã! <a href="/subscription/payment" className="underline font-bold">Assine agora</a></>
      ) : (
        <>{daysRemaining} dias restantes no período de teste. <a href="/subscription/payment" className="underline font-bold">Assine agora</a></>
      )}
    </div>
  );
});

export default SubscriptionProvider;
